<template>
	<div>
		<card
			tag="section"
			layout="metric"
			v-if="!report.loading && (metrics[0]?.length > 0 || metrics[1]?.length > 0)">
			<template #top>
				<chart-legend show-current :show-compare="metrics[1].length > 0" show-dashed></chart-legend>
			</template>
			<template #default>
				<metric-box
					metric="totalBillings"
					:results="metrics[0]"
					:compare="metrics[1]"
					:breakdown="breakdown"
					:report="report">
					Total Billings
				</metric-box>
				<metric-box
					metric="executive"
					:results="metrics[0]"
					:compare="metrics[1]"
					:breakdown="breakdown"
					:report="report">
					Executive Search
				</metric-box>
				<metric-box
					metric="consulting"
					:results="metrics[0]"
					:compare="metrics[1]"
					:breakdown="breakdown"
					:report="report">
					Leadership Consulting
				</metric-box>
				<metric-box
					metric="im"
					:results="metrics[0]"
					:compare="metrics[1]"
					:breakdown="breakdown"
					:report="report">
					Interim Management
				</metric-box>
				<metric-box
					metric="teamingValue"
					:results="metrics[0]"
					:compare="metrics[1]"
					:breakdown="breakdown"
					:report="report">
					Teaming Value
				</metric-box>
				<metric-box
					metric="teamingRevenue"
					:results="metrics[0]"
					:compare="metrics[1]"
					:breakdown="breakdown"
					:report="report">
					Teaming Revenue
				</metric-box>
				<metric-box
					metric="filledJobValue"
					:results="metrics[0]"
					:compare="metrics[1]"
					:breakdown="breakdown"
					:report="report">
					Average Filled Job Value
				</metric-box>
			</template>
		</card>
		<card
			v-if="
				(!report.filter.keys || report?.filter?.keys?.length == 0) &&
				breakdown?.breakdown &&
				breakdown?.breakdown?.breakdownFilter == ''
			"
			layout="metric"
			metric-size="sm"
			class="mt-5">
			<metric-row
				metric="partnerChange"
				:results="[{ partnerChange: details.partners.currentCount }]"
				:compare="[{ partnerChange: details.partners.currentCountStart }]"
				v-if="details.partners">
				Partner Change
			</metric-row>
			<metric-row
				metric="partner"
				:results="[{ partner: details.partners.currentCount }]"
				:compare="[{ partner: details.partners.previousCount }]"
				v-if="details.partners">
				<div class="metricWithComparison">
					Partners -
					<span class="twelveMonthComparison">12 month comparison</span>
				</div>
			</metric-row>
			<metric-row
				metric="researcher"
				:results="[{ researcher: details.researchers.current }]"
				:compare="[{ researcher: details.researchers.previous }]"
				v-if="details.researchers">
				<div class="metricWithComparison">
					Researchers -
					<span class="twelveMonthComparison">12 month comparison</span>
				</div>
			</metric-row>
			<metric-row
				metric="admin"
				:results="[{ admin: details.administration.current }]"
				:compare="[{ admin: details.administration.previous }]"
				v-if="details.administration">
				<div class="metricWithComparison">
					Administration -
					<span class="twelveMonthComparison">12 month comparison</span>
				</div>
			</metric-row>
		</card>
		<card
			layout="metric"
			metric-size="sm"
			class="mt-5"
			v-if="!report.loading && metrics[0]?.length > 0">
			<template v-slot:default>
				<metric-row
					metric="assignmentsTaken"
					:results="metrics[0]"
					:compare="metrics[1]"
					:breakdown="breakdown"
					:report="report">
					Assignments Taken
				</metric-row>
				<metric-row
					metric="teamingTaken"
					:results="metrics[0]"
					:compare="metrics[1]"
					:breakdown="breakdown"
					:report="report">
					Teaming Taken
				</metric-row>
				<metric-row
					metric="assignmentsFilled"
					:results="metrics[0]"
					:compare="metrics[1]"
					:breakdown="breakdown"
					:report="report">
					Assignments Filled
				</metric-row>
				<metric-row
					metric="teamingFilled"
					:results="metrics[0]"
					:compare="metrics[1]"
					:breakdown="breakdown"
					:report="report">
					Teaming Filled
				</metric-row>
				<metric-row
					metric="timeToFill"
					:results="metrics[0]"
					:compare="metrics[1]"
					:breakdown="breakdown"
					:report="report">
					Time to Fill
				</metric-row>
			</template>
		</card>
	</div>
</template>
<script>
	import { ref, computed, onMounted, inject } from "vue";
	import { currencyFormat, numberFormat } from "@/services/utils";
	import { GenericReport } from "@/assets/types";
	import metricBox from "@/components/webComponents/metricBox.vue";
	import metricRow from "@/components/webComponents/metricRow.vue";
	import { legend } from "@/components/charts";
	import { metrics_output } from "./metricsOutput.js";

	export default {
		components: {
			metricBox,
			metricRow,
			chartLegend: legend
		},
		props: {
			report: {
				type: GenericReport,
				default: () => {
					return new GenericReport("financial");
				}
			},
			breakdown: {
				type: Object,
				default: () => {
					return {};
				}
			},
			details: {
				type: Object,
				default: () => {
					return {};
				}
			}
		},
		setup(props, context) {
			const $financial = inject("$financial");

			const cformat = ref(currencyFormat.format);
			const nformat = ref(numberFormat.format);
			const pformat = ref(numberFormat.percent);

			const metrics = computed(() => {
				if (!props.breakdown.api) return [[], []];

				const filteredResults = props.breakdown.api.BreakdownFilterResults(props.report.results);
				const metrics = props.breakdown.api.CreateMetrics(filteredResults);

				const filteredResults_c = props.breakdown.api.BreakdownFilterResults(props.report.compare);
				const metrics_c = props.breakdown.api.CreateMetrics(filteredResults_c, { compare: true });

				metrics_output.value = {
					details: props.details,
					metrics: metrics,
					compare: metrics_c
				};

				return [metrics, metrics_c];
			});

			const currentPartnerCount = computed(() => {
				if (props.details.partners) {
					return props.details.partners.currentCount;
				}
				return 0;
			});
			const diffPartnerCount = computed(() => {
				if (props.details.partners) {
					const current = props.details.partners.currentCount;
					const previous = props.details.partners.previousCount;

					if (current.value === 0 && previous.value === 0) {
						return 0;
					} else if (previous.value === 0) {
						return 1;
					} else if (current.value === 0) {
						return previous.value > 0 ? -1 : 1;
					}

					return (current.value - previous.value) / Math.abs(previous.value);
				}
				return 0;
			});

			return {
				cformat,
				nformat,
				pformat,
				metrics,
				currentPartnerCount,
				diffPartnerCount
			};
		}
	};
</script>
<style lang="scss" scoped>
	// .metrics > .metric-box {
	// 	margin: 10px 3em 10px 0;
	// }
	.metricWithComparison {
		// white-space: nowrap;
		margin-right: -1.75rem;
		// padding-right: 0;
	}
	.twelveMonthComparison {
		line-break: none;
		white-space: nowrap;
		// text-align: right;
		// float: right;
		// font-size: smaller;
		font-size: 0.75em;
		font-weight: lighter;
		// display: block;
		position: relative;
		// top: -0.25rem;
		// top: 3.5rem;
		// bottom: 0.5rem;
		// position: absolute;
		// bottom: 0;
		// right: 0;
	}
</style>
